/*
 * Copyright (c) 2022. Nedaware SL
 */

import {FilterInterface, InvalidFilterProperty, InvalidFilterValue} from "../../api/filter-interface";
import {InternalPropertyMap} from "../basic-entity-interface/mapping-internal";
import {FilterGroup} from "../basic-entity-interface/mapping-external";

/**
 * Nedaware S.L.
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */

export abstract class BaseRangeFilter implements FilterInterface {
    protected _dataToParams(option: "lt" | "gt" | "lte" | "gte" | "between",
                            property: InternalPropertyMap, data: any[] | any): { [p: string]: string | string[] } {
        if (property == null) {
            throw new InvalidFilterProperty(property, "the property cannot be null");
        }
        const param = `${property.paramName}[${option}]`;
        if (option === "between") {
            if (!Array.isArray(data) || data.length !== 2) {
                throw new InvalidFilterValue(data, "for between range filter only an array of two elements is accepted.");
            }

            if (property.caster) {
                data = data.map(datum => {
                    datum = property.caster.fromModel(datum);
                    if (isNaN(datum)) {
                        throw new InvalidFilterValue(data, "data was expected to be a number after casting process");
                    }
                    return datum;
                });
            }

            return {[param]: `${data[0].toString(10)}..${data[1].toString(10)}`};
        } else {
            if (Array.isArray(data)) {
                throw new InvalidFilterValue(data, "arrays are not accepted for " + option);
            }

            if (property.caster) {
                data = property.caster.fromModel(data);
            }

            if (isNaN(data)) {
                throw new InvalidFilterValue(data, "data was expected to be a number after casting process");
            }

            return {[param]: data.toString()};
        }
    }

    abstract dataToParams(property: InternalPropertyMap, data: any[] | any): { [p: string]: string | string[] };
}

export class LowerThanRangeFilter extends BaseRangeFilter {
    dataToParams(property: InternalPropertyMap, data: any[] | any): { [p: string]: string | string[] } {
        return this._dataToParams("lt", property, data);
    }
}

export class GreaterThanRangeFilter extends BaseRangeFilter {
    dataToParams(property: InternalPropertyMap, data: any[] | any): { [p: string]: string | string[] } {
        return this._dataToParams("gt", property, data);
    }
}

export class LowerThanOrEqRangeFilter extends BaseRangeFilter {
    dataToParams(property: InternalPropertyMap, data: any[] | any): { [p: string]: string | string[] } {
        return this._dataToParams("lte", property, data);
    }
}

export class GreaterThanOrEqRangeFilter extends BaseRangeFilter {
    dataToParams(property: InternalPropertyMap, data: any[] | any): { [p: string]: string | string[] } {
        return this._dataToParams("gte", property, data);
    }
}

export class BetweenRangeFilter extends BaseRangeFilter {
    dataToParams(property: InternalPropertyMap, data: any[] | any): { [p: string]: string | string[] } {
        return this._dataToParams("between", property, data);
    }
}

/**
 * Range filter allows to filter by ranges of values
 */
export const RangeFilters: FilterGroup = [
    LowerThanRangeFilter, GreaterThanRangeFilter,
    LowerThanOrEqRangeFilter, GreaterThanOrEqRangeFilter,
    BetweenRangeFilter
];
